import * as React from "react";
import { Dispatch, AnyAction } from "redux";
import { connect } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { buttonIds, pageIds } from "@amzn/ziggy-asset";
import {
    station,
    RequiredReportingState,
    RequiredReportingProps,
    trackInfo,
    TimeRange,
    programmingPerformanceRequestPayload,
    hydrateCatalogPayload,
    timeRangePayload,
    countryInfo,
    TableRowProps,
    EntityType,
    trackStreamsInfo,
    trackListenersInfo,
    CatalogItemType,
    clientMetricsPayload,
    BundleMap,
} from "../../../models";
import {
    RootState,
    reportingActions,
    stationPerformanceScreenSelector,
    catalogActions,
    userActions,
    clientMetricsActions,
} from "../../../store";
import * as rootStyles from "../../styles";
import { stringIds, bundleIds } from "../../../assets";
import {
    Table,
    Loading,
    IncrementalLoading,
    ProfileHeadProps,
    ProfileHead,
    InlineError,
    VerticalSpacedTable,
    TableSeeAllButton,
    LineGraph,
    LineGraphProps,
} from "../../components";
import _ from "lodash";
import {
    getLocalizedString,
    generateQuery,
    generateSongRows,
    paths,
    HYDRATION_COUNT,
    guessTimeZone,
    generateCountryRows,
    SUMMARY_TABLE_ITEM_COUNT,
    generateQueryWithoutGranularity,
    parseTrackDataPoints,
    formatNumber,
    timeRangeToTickCount,
    formatDatum,
    formatTooltipDatum,
    hydrationHelpers,
    buildUIClickPayload,
} from "../../../utils";

const testIDPrefix = "StationPerformanceScreen";
const metricPrefix = "stationPerformancePage";

type ViewProps = {
    asin: string;
};

type StateProps = RequiredReportingProps & {
    tracks: trackInfo[];
    trackStreamInfo?: trackStreamsInfo;
    trackListenerInfo?: trackListenersInfo;
    topCountries: countryInfo[];
    selectedAsin: string;
    bundleMap: BundleMap;
};

type DispatchProps = {
    getStationPerformance: (
        request: programmingPerformanceRequestPayload
    ) => void;
    clearFailedAsins: () => void;
    hydrateAsins: (payload: hydrateCatalogPayload) => void;
    unselectAsin: (asin: string) => void;
    selectAsin: (asin: string) => void;
    updateCurrentPath: (payload: string) => void;
    sendClientMetrics: (payload: clientMetricsPayload) => void;
};

type Props = DispatchProps & StateProps & RouteComponentProps<ViewProps>;

type State = RequiredReportingState & {
    stationCatalogItem?: station;
    loadedCount: number;
};

class StationPerformanceScreen extends React.Component<Props, State> {
    private startTime = Date.now();

    constructor(props: Props) {
        super(props);
        this.state = {
            refreshing: false,
            showError: false,
            loadedCount: 0,
        };
        this.props.updateCurrentPath(window.location.pathname);
    }

    componentDidMount() {
        // If artist isnt selected, redirect to select artist page
        if (!this.props.selectedArtist) {
            this.props.history?.replace(paths.artistSelect);
            return;
        }
        this.setState({ finishedInitialLoad: false });
        const asin = this.props.match.params.asin;
        if (!asin) {
            return;
        }

        this.startTime = Date.now();

        this.props.selectAsin(asin);

        const stationCatalogItem = this.props.catalog.get(asin) as station;
        if (stationCatalogItem) {
            this.setState({ stationCatalogItem });
        }
        this.getStationPerformance();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.selectedRange !== this.props.selectedRange) {
            this.getStationPerformance();
        } else if (
            (this.props.selectedRange === TimeRange.Custom &&
                this.props.startDate !== prevProps.startDate) ||
            this.props.endDate !== prevProps.endDate
        ) {
            this.getStationPerformance();
        }

        if (prevProps.inProgress && !this.props.inProgress) {
            this.setState({ refreshing: false, loadedReports: true });
        }

        // Loaded everything for the first time
        if (
            this.state.finishedInitialLoad === false &&
            this.props.catalogBuildCompleted &&
            this.state.loadedReports &&
            this.props.hydrationInProgress &&
            !prevProps.hydrationInProgress
        ) {
            this.setState({ finishedInitialLoad: true });
        }

        if (this.props.error !== prevProps.error) {
            if (
                this.props.error &&
                this.props.error.requestPath === paths.stationPerformance
            ) {
                this.setState({ showError: true });
            } else {
                this.setState({ showError: false });
            }
        }

        if (
            prevProps.tracks.length < this.props.tracks.length ||
            this.state.loadedCount < this.props.tracks.length
        ) {
            this.loadMore();
        }
    }

    render() {
        // Loading state
        if (this.props.inProgress && !this.state.refreshing) {
            return <Loading />;
        }

        const streamCount =
            (this.props.trackStreamInfo &&
                this.props.trackStreamInfo.totalCount) ||
            0;
        const userTimeZone = guessTimeZone();

        const streamLineGraphProps: LineGraphProps | undefined = this.props
            .trackStreamInfo && {
            data: [
                parseTrackDataPoints(
                    this.props.trackStreamInfo,
                    this.props.selectedRange === TimeRange.AllTime
                ),
            ],
            title: getLocalizedString(
                this.props.bundleMap,
                {
                    bundleId: bundleIds.REPORTS_STRINGS,
                    stringId: stringIds.Reports.totalStreamsTitle,
                },
                { "0": formatNumber(streamCount) }
            ),
            labels:
                this.props.selectedRange === TimeRange.AllTime
                    ? [
                          getLocalizedString(this.props.bundleMap, {
                              bundleId: bundleIds.REPORTS_STRINGS,
                              stringId: stringIds.Reports.allTimeSubtitle,
                          }),
                      ]
                    : undefined,
            xTickCount: timeRangeToTickCount(this.props.selectedRange),
            tickFormat: (datum: any) =>
                formatDatum(datum, this.props.selectedRange, userTimeZone),
            tooltipFormat: (datum: any) =>
                formatTooltipDatum(
                    datum,
                    this.props.selectedRange,
                    userTimeZone
                ),
            id: testIDPrefix + "_StreamLineGraph",
            bundleMap: this.props.bundleMap,
        };

        const listenersCount =
            (this.props.trackListenerInfo &&
                this.props.trackListenerInfo.totalCount) ||
            0;

        const listenerLineGraphProps: LineGraphProps | undefined = this.props
            .trackListenerInfo && {
            data: [
                parseTrackDataPoints(
                    this.props.trackListenerInfo,
                    this.props.selectedRange === TimeRange.AllTime
                ),
            ],
            title: getLocalizedString(
                this.props.bundleMap,
                {
                    bundleId: bundleIds.REPORTS_STRINGS,
                    stringId: stringIds.Reports.totalListenersTitle,
                },
                { "0": formatNumber(listenersCount) }
            ),
            labels:
                this.props.selectedRange === TimeRange.AllTime
                    ? [
                          getLocalizedString(this.props.bundleMap, {
                              bundleId: bundleIds.REPORTS_STRINGS,
                              stringId: stringIds.Reports.allTimeSubtitle,
                          }),
                      ]
                    : undefined,
            xTickCount: timeRangeToTickCount(this.props.selectedRange),
            tickFormat: (datum: any) =>
                formatDatum(datum, this.props.selectedRange, userTimeZone),
            tooltipFormat: (datum: any) =>
                formatTooltipDatum(
                    datum,
                    this.props.selectedRange,
                    userTimeZone
                ),
            id: testIDPrefix + "_ListenerLineGraph",
            bundleMap: this.props.bundleMap,
        };

        const station = this.state.stationCatalogItem;

        let profileHeadProps: ProfileHeadProps = {
            imageSource: "",
            title: "",
            subtitle: getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.stationsLabel,
            }),
            id: `${testIDPrefix}_ProfileHead`,
        };

        if (station) {
            profileHeadProps = {
                ...profileHeadProps,
                imageSource:
                    station && station?.images?.artMedium
                        ? station?.images?.artMedium
                        : "",
                title: station.title || "",
                imageDescription: station.title,
            };
        }

        const songRows = generateSongRows(
            this.props.tracks,
            this.props.catalog,
            this.props.hydratingAsins,
            this.props.failedAsins,
            this.props.history?.push,
            this.props.bundleMap,
            testIDPrefix + "_SongRows",
            false
        );

        // Top countries props
        const topCountriesHeader = [
            getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.countriesTitle,
            }),
            "",
            getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.streamsLabel,
            }),
        ];
        const topCountries: TableRowProps[] = generateCountryRows(
            this.props.topCountries,
            testIDPrefix + "_TopCountriesRows"
        );

        const topCountriesRows = topCountries.slice(
            0,
            SUMMARY_TABLE_ITEM_COUNT
        );

        return (
            <Container
                fluid={true}
                className="rootContainer"
                style={rootStyles.containerStyles.rootViewContainer}
            >
                {/* <Image
                    blurRadius={rootStyles.blurAmount}
                    style={rootStyles.imageStyles.backgroundImage}
                    source={{ uri: profileHeadProps.imageSource }}
                /> */}
                {this.state.stationCatalogItem && (
                    <ProfileHead {...profileHeadProps} />
                )}

                <Row>
                    {this.state.showError && (
                        <InlineError
                            text={getLocalizedString(this.props.bundleMap, {
                                bundleId: bundleIds.ERRORS_STRINGS,
                                stringId: stringIds.Errors.reportsError,
                            })}
                            retryAction={this.getStationPerformance}
                            id={`${testIDPrefix}_Error`}
                        />
                    )}
                </Row>

                <Row>
                    <Col xs={12} md={6}>
                        {/* song streams */}
                        {streamLineGraphProps && (
                            <LineGraph
                                key="songStreams"
                                {...streamLineGraphProps}
                            />
                        )}
                    </Col>
                    <Col xs={12} md={6}>
                        {/* song listeners */}
                        {listenerLineGraphProps && (
                            <LineGraph
                                key="songListeners"
                                {...listenerLineGraphProps}
                            />
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Table
                            loading={this.state.refreshing}
                            key="stationTracks"
                            title={getLocalizedString(this.props.bundleMap, {
                                bundleId: bundleIds.REPORTS_STRINGS,
                                stringId:
                                    stringIds.Reports.stationStreamDetailsTitle,
                            })}
                            labels={[
                                getLocalizedString(this.props.bundleMap, {
                                    bundleId: bundleIds.REPORTS_STRINGS,
                                    stringId: stringIds.Reports.songsLabel,
                                }),
                                "",
                                getLocalizedString(this.props.bundleMap, {
                                    bundleId: bundleIds.REPORTS_STRINGS,
                                    stringId: stringIds.Reports.streamsLabel,
                                }),
                            ]}
                            rows={songRows}
                            showIndex={false}
                            loadMore={this.loadMore}
                            scrolling={false}
                            footer={
                                this.props.tracks.length <=
                                this.state.loadedCount ? (
                                    <div
                                        style={{
                                            height: rootStyles.spacers.huge,
                                        }}
                                    />
                                ) : (
                                    <IncrementalLoading />
                                )
                            }
                            id={testIDPrefix + "_SongTable"}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        {/* Top Countries */}
                        {
                            <VerticalSpacedTable
                                rows={topCountriesRows}
                                labels={topCountriesHeader}
                                showIndex={true}
                                title={getLocalizedString(
                                    this.props.bundleMap,
                                    {
                                        bundleId: bundleIds.REPORTS_STRINGS,
                                        stringId:
                                            stringIds.Reports.topCountriesTitle,
                                    }
                                )}
                                id={testIDPrefix + "_TopCountriesTable"}
                                footer={
                                    <TableSeeAllButton
                                        totalRows={topCountries.length}
                                        id={testIDPrefix + "_seeAllCountries"}
                                        onClick={this.seeAllCountries}
                                    />
                                }
                                emptyMessage={getLocalizedString(
                                    this.props.bundleMap,
                                    {
                                        bundleId: bundleIds.EMPTYSTATE_STRINGS,
                                        stringId: stringIds.EmptyState.message,
                                    }
                                )}
                            />
                        }
                    </Col>
                </Row>
            </Container>
        );
    }

    private loadMore = () => {
        if (!this.props.signedIn) {
            return;
        }

        const nextCount: number = hydrationHelpers.hydrateAsinsBatch(
            this.state.loadedCount,
            this.props.tracks,
            this.props.hydrateAsins,
            CatalogItemType.Tracks,
            this.props.userLocale
        );

        this.setState({
            loadedCount: nextCount,
        });
    };

    private getStationPerformance = () => {
        if (
            !this.props.signedIn ||
            !this.props.selectedArtist ||
            !this.props.teamId
        ) {
            return;
        }

        this.setState({
            loadedCount: HYDRATION_COUNT,
        });

        const asin = this.props.match.params.asin;
        if (!asin) {
            return;
        }

        const stationId = asin;

        const timeRangePayload: timeRangePayload = {
            timeRange: this.props.selectedRange,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
        };

        const payload: programmingPerformanceRequestPayload = {
            selectionSourceId: stationId,
            timeRange: this.props.selectedRange,
            query: {
                artistAsin: this.props.selectedArtist,
                topTracksQueryV2: {
                    selectionSourceId: stationId,
                    stationKey: stationId,
                    ...generateQuery(timeRangePayload, guessTimeZone(), 300),
                },
                topCountriesForEntityStreamsQuery: {
                    ...generateQueryWithoutGranularity(
                        timeRangePayload,
                        guessTimeZone()
                    ),
                    entityType: EntityType.Station,
                    entityId: stationId,
                },
                trackStreamsQueryV2: {
                    ...generateQuery(timeRangePayload, guessTimeZone()),
                    selectionSourceId: stationId,
                },
                trackListenersQueryV2: {
                    ...generateQuery(timeRangePayload, guessTimeZone()),
                    selectionSourceId: stationId,
                },
            },
            requestPath: paths.stationPerformance,
            teamId: this.props.teamId,
        };

        this.props.getStationPerformance(payload);
    };

    private seeAllCountries = () => {
        const asin = this.props.match.params.asin;
        if (!asin) {
            return;
        }
        this.props.sendClientMetrics(
            buildUIClickPayload(
                buttonIds.Reports.seeAllCountries,
                pageIds.stationPerformance,
                asin,
                EntityType.Station
            )
        );
        this.props.history.push(`${paths.stationCountries}/${asin}`);
    };
}

function mapStateToProps(state: RootState) {
    return stationPerformanceScreenSelector(state, paths.stationPerformance);
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return {
        getStationPerformance: (
            request: programmingPerformanceRequestPayload
        ) => dispatch(reportingActions.getStationPerformance(request)),
        clearFailedAsins: () => dispatch(catalogActions.clearFailedAsins()),
        hydrateAsins: (payload: hydrateCatalogPayload) =>
            dispatch(catalogActions.hydrateAsins(payload)),
        selectAsin: (asin: string) =>
            dispatch(reportingActions.setSelectedStationAsin(asin)),
        unselectAsin: (asin: string) =>
            dispatch(reportingActions.removeSelectedStationAsin(asin)),
        updateCurrentPath: (payload: string) =>
            dispatch(userActions.updateCurrentPath(payload)),
        sendClientMetrics: (payload: clientMetricsPayload) =>
            dispatch(clientMetricsActions.sendClientMetrics(payload)),
    };
}

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(StationPerformanceScreen)
);
